import React, { useEffect, useState } from "react";
import { useOutletContext } from "react-router-dom";
import api, { apiError } from "@/api";
import { useAuth } from "@/context/AuthContext";
import { DataTable } from "@/components/DataTable";
import { PageHeader } from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/StatusBadge";
import { EVENT_STATUS, fmtDate, CURRENCIES } from "@/lib/format";
import { Plus, Lock, Trash2, Sparkles } from "lucide-react";
import { toast } from "sonner";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger,
} from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const STATUSES = ["draft", "open_po", "shopping_live", "shipping", "finished", "reconciled"];
const empty = { code: "", country: "", currency: "JPY", open_date: "", close_date: "", shopper_id: "", status: "draft", default_kurs: "", budget_alert_pct: 10 };

export default function Events() {
  const { events, reloadEvents } = useOutletContext();
  const { user } = useAuth();
  const [users, setUsers] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(empty);
  const [editId, setEditId] = useState(null);
  const isAdmin = user.role === "admin";
  const canManage = isAdmin || ["ops", "shopper", "sales"].some((r) => user.roles.includes(r));
  const hasDemo = events.some((e) => e.is_demo);
  const [demoBusy, setDemoBusy] = useState(false);
  const createDemo = async () => {
    setDemoBusy(true);
    try {
      const { data } = await api.post("/admin/demo/create");
      toast.success(`Event contoh dibuat: ${data.events.join(", ")} — 4 master produk dipakai bersama dengan harga JPY & KRW berbeda`);
      reloadEvents();
    } catch (e) { toast.error(apiError(e)); } finally { setDemoBusy(false); }
  };
  const deleteDemo = async () => {
    if (!window.confirm("Hapus semua event contoh (DEMO-JP-TOKYO, DEMO-KR-SEOUL) beserta katalog, customer, order, dan pembayaran contohnya? Data asli tidak tersentuh.")) return;
    setDemoBusy(true);
    try {
      const { data } = await api.delete("/admin/demo");
      const n = Object.values(data.removed).reduce((a, b) => a + b, 0);
      toast.success(`Event contoh dihapus (${n} record dibersihkan)`);
      reloadEvents();
    } catch (e) { toast.error(apiError(e)); } finally { setDemoBusy(false); }
  };
  const remove = async (ev) => {
    if (!window.confirm(`Hapus event ${ev.code}? Katalog, catatan belanja, dan biaya event ini ikut terhapus.`)) return;
    try { await api.delete(`/events/${ev.id}`); toast.success("Event dihapus"); reloadEvents(); }
    catch (e) { toast.error(apiError(e)); }
  };

  useEffect(() => { api.get("/users").then((r) => setUsers(r.data.filter((u) => u.roles?.includes("shopper")))); }, []);

  const openNew = () => { setForm(empty); setEditId(null); setOpen(true); };
  const openEdit = (e) => {
    setForm({ code: e.code, country: e.country, currency: e.currency,
      open_date: e.open_date?.slice(0, 10), close_date: e.close_date?.slice(0, 10),
      shopper_id: e.shopper_id || "", status: e.status, default_kurs: e.default_kurs || "", budget_alert_pct: e.budget_alert_pct ?? 10 });
    setEditId(e.id); setOpen(true);
  };

  const save = async () => {
    try {
      const payload = { ...form, default_kurs: Number(form.default_kurs) || 0, budget_alert_pct: Number(form.budget_alert_pct) || 0,
        open_date: new Date(form.open_date).toISOString(),
        close_date: new Date(form.close_date).toISOString() };
      if (editId) await api.put(`/events/${editId}`, payload);
      else await api.post("/events", payload);
      toast.success(editId ? "Event diperbarui" : "Event dibuat");
      setOpen(false); reloadEvents();
    } catch (e) { toast.error(apiError(e)); }
  };

  const columns = [
    { key: "code", label: "Kode Event", mono: true, render: (r) => <span className="font-mono font-semibold text-slate-900">{r.code}{r.is_demo && <span data-testid={`demo-badge-${r.code}`} className="ml-2 text-[9px] font-sans font-bold px-1.5 py-0.5 rounded bg-amber-100 text-amber-800 align-middle">CONTOH</span>}</span> },
    { key: "country", label: "Negara" },
    { key: "currency", label: "Mata Uang", mono: true },
    { key: "default_kurs", label: "Kurs Default (IDR)", mono: true, render: (r) => r.default_kurs ? `Rp ${Number(r.default_kurs).toLocaleString("id-ID")}` : <span className="text-slate-300">—</span>, raw: (r) => r.default_kurs || "" },
    { key: "open_date", label: "Buka", render: (r) => fmtDate(r.open_date), raw: (r) => fmtDate(r.open_date) },
    { key: "close_date", label: "Tutup", render: (r) => fmtDate(r.close_date), raw: (r) => fmtDate(r.close_date) },
    { key: "order_count", label: "Order", mono: true },
    { key: "status", label: "Status", render: (r) => (
      <span className={`text-[11px] font-semibold uppercase px-2 py-0.5 rounded border ${EVENT_STATUS[r.status] || EVENT_STATUS.draft}`}>{r.status?.replace("_", " ")}</span>
    ) },
    { key: "locked", label: "Kunci", render: (r) => r.locked ? <Badge label="Locked" cls="bg-rose-50 text-rose-700 border-rose-300" /> : <span className="text-slate-300">—</span> },
    { key: "actions", label: "", sortable: false, noExport: true, render: (r) => canManage && (
      <div className="flex items-center gap-1">
        <Button data-testid={`edit-event-${r.code}`} size="sm" variant="outline" className="h-8" onClick={() => openEdit(r)} disabled={r.locked}>
          {r.locked ? <Lock className="w-3 h-3" /> : "Edit"}
        </Button>
        {isAdmin && r.order_count === 0 && <button data-testid={`delete-event-${r.code}`} title="Hapus event" className="p-1.5 text-rose-400 hover:text-rose-600" onClick={() => remove(r)}><Trash2 className="w-4 h-4" /></button>}
      </div>
    ) },
  ];

  return (
    <div>
      <PageHeader title="Event Jastip" subtitle="Satu event = satu negara & satu mata uang. Terkunci 1 bulan setelah tanggal tutup.">
        {canManage && <Button data-testid="btn-add-event" onClick={openNew} className="gap-1.5 bg-slate-900 hover:bg-slate-800"><Plus className="w-4 h-4" /> Buat Event</Button>}
        {isAdmin && !hasDemo && <Button data-testid="btn-create-demo" variant="outline" onClick={createDemo} disabled={demoBusy} className="gap-1.5 border-dashed"><Sparkles className="w-4 h-4" /> {demoBusy ? "Membuat..." : "Buat Event Contoh"}</Button>}
        {isAdmin && hasDemo && <Button data-testid="btn-delete-demo" variant="outline" onClick={deleteDemo} disabled={demoBusy} className="gap-1.5 border-rose-300 text-rose-700 hover:bg-rose-50"><Trash2 className="w-4 h-4" /> {demoBusy ? "Menghapus..." : "Hapus Event Contoh"}</Button>}
      </PageHeader>
      <DataTable testId="events-table" columns={columns} data={events} searchKeys={["code", "country", "currency", "status"]} exportName="events" />

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader><DialogTitle>{editId ? "Edit Event" : "Buat Event Baru"}</DialogTitle></DialogHeader>
          <div className="grid grid-cols-2 gap-3">
            <div className="col-span-2"><Label>Kode Event</Label><Input data-testid="event-code" value={form.code} onChange={(e) => setForm({ ...form, code: e.target.value })} placeholder="JP-2026-TOKYO" /></div>
            <div><Label>Negara Asal</Label><Input data-testid="event-country" value={form.country} onChange={(e) => setForm({ ...form, country: e.target.value })} placeholder="Japan" /></div>
            <div><Label>Mata Uang</Label>
              <Select value={form.currency} onValueChange={(v) => setForm({ ...form, currency: v })}>
                <SelectTrigger data-testid="event-currency"><SelectValue /></SelectTrigger>
                <SelectContent className="max-h-72">{CURRENCIES.map((c) => <SelectItem key={c.code} value={c.code} data-testid={`currency-option-${c.code}`}><span className="font-mono font-semibold">{c.code}</span> <span className="text-slate-500 ml-1">{c.name}</span></SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label>Kurs Default (1 {form.currency} = ? IDR)</Label><Input data-testid="event-kurs" type="number" step="any" value={form.default_kurs} onChange={(e) => setForm({ ...form, default_kurs: e.target.value })} placeholder="mis. 105.5" /><p className="text-[11px] text-slate-400 mt-1">Kurs awal modal katalog; bisa di-override per item & per belanja.</p></div>
            <div><Label>Batas Alert Modal (%)</Label><Input data-testid="event-budget-pct" type="number" step="any" value={form.budget_alert_pct} onChange={(e) => setForm({ ...form, budget_alert_pct: e.target.value })} /><p className="text-[11px] text-slate-400 mt-1">Shopper diperingatkan jika harga beli melebihi estimasi katalog lebih dari persen ini.</p></div>
            <div><Label>Tanggal Buka</Label><Input data-testid="event-open" type="date" value={form.open_date} onChange={(e) => setForm({ ...form, open_date: e.target.value })} /></div>
            <div><Label>Tanggal Tutup</Label><Input data-testid="event-close" type="date" value={form.close_date} onChange={(e) => setForm({ ...form, close_date: e.target.value })} /></div>
            <div><Label>Ops PJ (Sales & Shopper)</Label>
              <Select value={form.shopper_id} onValueChange={(v) => setForm({ ...form, shopper_id: v })}>
                <SelectTrigger data-testid="event-shopper"><SelectValue placeholder="Pilih shopper" /></SelectTrigger>
                <SelectContent>{users.map((u) => <SelectItem key={u.id} value={u.id}>{u.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label>Status</Label>
              <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}>
                <SelectTrigger data-testid="event-status"><SelectValue /></SelectTrigger>
                <SelectContent>{STATUSES.map((s) => <SelectItem key={s} value={s}>{s.replace("_", " ")}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter><Button data-testid="save-event" onClick={save} className="bg-slate-900 hover:bg-slate-800">Simpan</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
